import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { checkForMissingSlots, debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { ThemeConfig } from '@nylas/core';
/**
 * The `nylas-event-limits` component is an input form for event limits, such as the number of available days in the future and
 * the minimum period of notice that a guest must provide to cancel a booking.
 * This component is a container for the `nylas-limit-future-bookings` and `nylas-min-cancellation-notice` components.
 *
 * ```html
 * <nylas-event-limits>
 *   <div slot="inputs">
 *     <nylas-limit-future-bookings></nylas-limit-future-bookings>
 *     <nylas-min-cancellation-notice></nylas-min-cancellation-notice>
 *   </div>
 * </nylas-event-limits>
 * ```
 *
 * @slot inputs - The event information inputs. This includes the limit future bookings and minimum cancellation notice components.
 */
@Component({
  tag: 'nylas-event-limits',
  styleUrl: 'nylas-event-limits.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasEventLimits {
  @Element() el!: HTMLElement;

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.el.style.setProperty(`${key}`, value);
      }
    }
  }

  connectedCallback() {
    debug('nylas-event-limits', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.el.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    debug('nylas-event-limits', 'componentWillLoad');
  }

  componentDidLoad() {
    debug('nylas-event-limits', 'componentDidLoad');
    checkForMissingSlots(['inputs'], this.el);
  }

  disconnectedCallback() {
    debug('nylas-event-limits', 'disconnectedCallback');
  }

  @RegisterComponent<NylasEventLimits, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-event-limits',
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {},
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host>
        <nylas-form-card exportparts="nfc__header: nel__header" themeConfig={this.themeConfig}>
          <h1 slot="header-title" class="nylas-event-limits__title">
            {i18next.t('nylasEventLimits.headerTitle')}
          </h1>
          <div slot="content" class="nylas-event-limits__content">
            <slot name="inputs"></slot>
          </div>
        </nylas-form-card>
      </Host>
    );
  }
}
